import type {
  ControlCenterAsset,
  ControlCenterJob,
  ControlCenterLicenseType,
  ControlCenterMunicipality,
  ControlCenterMunicipalityLevel,
  ControlCenterPerson,
} from '../../types/controlCenter'
import {
  resolveJurisdictionsAtPoint,
  type ResolvedJurisdiction,
  type TigerwebBoundaryLevel,
} from '../../services/tigerwebBoundaryService'
import { licenseTypeLabel } from '../../utils/controlCenterMunicipalityLicenses'
import { executeCapability } from '../../utils/capabilityExecute'
import { aggregateForecast } from '../kioskWeather/weatherForecastUtils'
import { certDisplayLabel } from '../../services/profileService'

export type EntityCapability =
  | 'hasIdentity'
  | 'hasLocation'
  | 'hasAddress'
  | 'hasStatus'
  | 'hasAssignee'
  | 'hasBoundary'
  | 'hasFuelLevel'
  | 'hasBattery'
  | 'hasContacts'
  | 'hasSchedule'
  | 'hasInventoryLink'
  | 'hasTimesheet'
  | 'hasPortalUser'

export type OperationEntityKind = 'job' | 'person' | 'asset' | 'municipality' | 'place'

export type OperationEntity = {
  kind: OperationEntityKind
  id: string
  name: string
  lat: number
  lng: number
  capabilities: Set<EntityCapability>
  job?: ControlCenterJob
  person?: ControlCenterPerson
  asset?: ControlCenterAsset
  municipality?: ControlCenterMunicipality
}

export type OperatorId =
  | 'eta'
  | 'nearby'
  | 'air-miles'
  | 'contains'
  | 'fuel-planning'
  | 'local-license-check'
  | 'forecast'
  | 'weather-forecast'
  | 'time'
  | 'cert-check'

/** Portal map layers that Nearby can search in-app. */
export type NearbyLayerTarget =
  | 'assets'
  | 'jobs'
  | 'people'
  | 'municipalities'

/**
 * Nearby search target: a portal layer id, or a Google Places Table-1 type
 * (e.g. `gas_station`, `car_repair`).
 */
export type NearbyTarget = NearbyLayerTarget | string

export type NearbyTargetOption = {
  id: NearbyTarget
  label: string
  group: 'Layers' | 'Establishments'
  keywords?: string
}

export const NEARBY_LAYER_TARGETS: NearbyLayerTarget[] = [
  'jobs',
  'people',
  'assets',
  'municipalities',
]

export function isNearbyLayerTarget(target: string): target is NearbyLayerTarget {
  return (NEARBY_LAYER_TARGETS as string[]).includes(target)
}

/** Searchable Find Nearby options: each Input layer + Google establishment types. */
export const NEARBY_TARGET_OPTIONS: NearbyTargetOption[] = [
  {
    id: 'jobs',
    label: 'Jobs',
    group: 'Layers',
    keywords: 'sites projects jobtread',
  },
  {
    id: 'people',
    label: 'People',
    group: 'Layers',
    keywords: 'employees crew clocked',
  },
  {
    id: 'assets',
    label: 'GPS Assets',
    group: 'Layers',
    keywords: 'vehicles fleet trucks linxup',
  },
  {
    id: 'municipalities',
    label: 'Municipalities',
    group: 'Layers',
    keywords: 'cities licenses jurisdictions',
  },
  // Google Places types (Table 1) useful for field ops
  {
    id: 'gas_station',
    label: 'Gas stations',
    group: 'Establishments',
    keywords: 'fuel petrol diesel pump',
  },
  {
    id: 'car_repair',
    label: 'Mechanics / car repair',
    group: 'Establishments',
    keywords: 'auto shop garage service',
  },
  {
    id: 'car_wash',
    label: 'Car washes',
    group: 'Establishments',
    keywords: 'auto wash detail',
  },
  {
    id: 'parking',
    label: 'Parking',
    group: 'Establishments',
    keywords: 'lot garage',
  },
  {
    id: 'lodging',
    label: 'Lodging / hotels',
    group: 'Establishments',
    keywords: 'hotel motel inn stay overnight',
  },
  {
    id: 'rv_park',
    label: 'RV parks',
    group: 'Establishments',
    keywords: 'campground camping',
  },
  {
    id: 'campground',
    label: 'Campgrounds',
    group: 'Establishments',
    keywords: 'camping tent',
  },
  {
    id: 'restaurant',
    label: 'Restaurants',
    group: 'Establishments',
    keywords: 'food dining eat',
  },
  {
    id: 'cafe',
    label: 'Cafes',
    group: 'Establishments',
    keywords: 'coffee breakfast',
  },
  {
    id: 'convenience_store',
    label: 'Convenience stores',
    group: 'Establishments',
    keywords: 'corner store snacks',
  },
  {
    id: 'grocery_or_supermarket',
    label: 'Grocery / supermarket',
    group: 'Establishments',
    keywords: 'food market',
  },
  {
    id: 'hardware_store',
    label: 'Hardware stores',
    group: 'Establishments',
    keywords: 'tools lumber supplies',
  },
  {
    id: 'home_goods_store',
    label: 'Home goods stores',
    group: 'Establishments',
    keywords: 'supplies',
  },
  {
    id: 'storage',
    label: 'Storage',
    group: 'Establishments',
    keywords: 'self storage unit warehouse',
  },
  {
    id: 'laundry',
    label: 'Laundry',
    group: 'Establishments',
    keywords: 'laundromat dry clean',
  },
  {
    id: 'electrician',
    label: 'Electricians',
    group: 'Establishments',
    keywords: 'electrical contractor',
  },
  {
    id: 'plumber',
    label: 'Plumbers',
    group: 'Establishments',
    keywords: 'plumbing',
  },
  {
    id: 'roofing_contractor',
    label: 'Roofing contractors',
    group: 'Establishments',
    keywords: 'roofer',
  },
  {
    id: 'general_contractor',
    label: 'General contractors',
    group: 'Establishments',
    keywords: 'builder construction',
  },
  {
    id: 'locksmith',
    label: 'Locksmiths',
    group: 'Establishments',
    keywords: 'keys lock',
  },
  {
    id: 'moving_company',
    label: 'Moving companies',
    group: 'Establishments',
    keywords: 'movers truck rental',
  },
  {
    id: 'hospital',
    label: 'Hospitals',
    group: 'Establishments',
    keywords: 'emergency medical er',
  },
  {
    id: 'pharmacy',
    label: 'Pharmacies',
    group: 'Establishments',
    keywords: 'drugstore cvs walgreens',
  },
  {
    id: 'doctor',
    label: 'Doctors / urgent care',
    group: 'Establishments',
    keywords: 'clinic medical urgent care',
  },
  {
    id: 'police',
    label: 'Police stations',
    group: 'Establishments',
    keywords: 'cops law enforcement',
  },
  {
    id: 'fire_station',
    label: 'Fire stations',
    group: 'Establishments',
    keywords: 'fire department',
  },
  {
    id: 'bank',
    label: 'Banks',
    group: 'Establishments',
    keywords: 'atm finance',
  },
  {
    id: 'atm',
    label: 'ATMs',
    group: 'Establishments',
    keywords: 'cash money',
  },
  {
    id: 'post_office',
    label: 'Post offices',
    group: 'Establishments',
    keywords: 'mail usps shipping',
  },
  {
    id: 'airport',
    label: 'Airports',
    group: 'Establishments',
    keywords: 'flight travel',
  },
]

export type ForecastType = 'active' | 'inactive' | 'starting' | 'ending'

export type OperatorArgKey =
  | 'destination'
  | 'nearbyTarget'
  | 'radiusMiles'
  | 'rangeMiles'
  | 'forecastType'
  | 'forecastStartDate'
  | 'forecastEndDate'
  | 'timeStartDate'
  | 'timeEndDate'

export type OperatorDef = {
  id: OperatorId
  label: string
  description: string
  requires: EntityCapability[]
  args: OperatorArgKey[]
}

export type OperationMapPoint = {
  id: string
  name: string
  subtitle?: string
  lat: number
  lng: number
  color: string
  /** When set, selecting this result can pin the live portal entity under omni search. */
  sourceEntity?: {
    category: 'job' | 'person' | 'asset' | 'municipality'
    id: string
  }
}

export type OperationResultTable = {
  columns: Array<{ key: string; label: string; align?: 'left' | 'right' }>
  rows: Array<Record<string, string>>
  footer?: Record<string, string>
  /**
   * When set, each row’s `id` field is a portal entity id of this category
   * (used for click-to-pin from Time breakdown tables).
   */
  rowEntityCategory?: 'job' | 'person'
  emptyMessage?: string
}

export type OperationResult = {
  operatorId: OperatorId
  title: string
  summary: string
  entityId: string
  entityName: string
  ranAt: number
  rows: Array<{ label: string; value: string }>
  mapPoints: OperationMapPoint[]
  routeLine: Array<[number, number]> | null
  /** Google Maps directions URL for ETA (and similar) results. */
  mapsDirectionsUrl?: string
  /** Optional breakdown table (e.g. Time hours by employee/job). */
  table?: OperationResultTable
}

/** Google Maps directions deep link (origin → destination). */
export function googleMapsDirectionsUrl(args: {
  originLat: number
  originLng: number
  destLat: number
  destLng: number
  travelMode?: 'driving' | 'walking' | 'bicycling' | 'transit'
}): string {
  const params = new URLSearchParams({
    api: '1',
    origin: `${args.originLat},${args.originLng}`,
    destination: `${args.destLat},${args.destLng}`,
    travelmode: args.travelMode ?? 'driving',
  })
  return `https://www.google.com/maps/dir/?${params.toString()}`
}

/** Google Maps pin / search link for a single lat/lng. */
export function googleMapsLocationUrl(lat: number, lng: number): string {
  const params = new URLSearchParams({
    api: '1',
    query: `${lat},${lng}`,
  })
  return `https://www.google.com/maps/search/?${params.toString()}`
}

export type OperationInputs = {
  rangeMiles: number
  radiusMiles: number
  destinationMode: 'address' | 'entity'
  destinationAddress: string
  /** `kind:id` e.g. `job:22NjhPYF25h6` */
  destinationEntityRef: string
  nearbyTarget: NearbyTarget
  forecastType: ForecastType
  /** YYYY-MM-DD */
  forecastStartDate: string
  /** YYYY-MM-DD */
  forecastEndDate: string
  /** YYYY-MM-DD — Time operator range start */
  timeStartDate: string
  /** YYYY-MM-DD — Time operator range end */
  timeEndDate: string
}

export const DEFAULT_OPERATION_INPUTS: OperationInputs = {
  rangeMiles: 80,
  radiusMiles: 25,
  destinationMode: 'entity',
  destinationAddress: '',
  destinationEntityRef: '',
  nearbyTarget: 'assets',
  forecastType: 'active',
  forecastStartDate: '',
  forecastEndDate: '',
  timeStartDate: '',
  timeEndDate: '',
}

/** Map-selection categories usable as a step input (excludes synthetic Places pins). */
export type OperationSelectionCategory = Exclude<OperationEntityKind, 'place'>

export type OperationStepInputSource =
  | { kind: 'selection'; category: OperationSelectionCategory }
  | { kind: 'priorPoint'; fromStepId: string; pointId: string }
  /** Embedded snapshot of a map result pin (Places, etc.) so it survives when the step result is replaced. */
  | { kind: 'mapPoint'; point: OperationMapPoint }

export type OperationStep = {
  id: string
  operatorId: OperatorId | ''
  inputs: OperationInputs
  inputSource: OperationStepInputSource | null
  result: OperationResult | null
}

export function createEmptyOperationStep(): OperationStep {
  return {
    id: `step-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    operatorId: '',
    inputs: { ...DEFAULT_OPERATION_INPUTS },
    inputSource: null,
    result: null,
  }
}

function resetOperationStep(step: OperationStep): OperationStep {
  return {
    ...step,
    inputSource: null,
    operatorId: '' as const,
    inputs: { ...DEFAULT_OPERATION_INPUTS },
    result: null,
  }
}

/**
 * Clear results whose parent portal entity was closed (and reset mapPoint-backed
 * steps whose source entity matches). Dependent priorPoint steps are handled by
 * the existing broken-link effect once parent results are cleared.
 */
export function clearOperationResultsForParentEntities(
  steps: OperationStep[],
  entityIds: ReadonlySet<string>,
): OperationStep[] {
  if (entityIds.size === 0) return steps
  let changed = false
  const next = steps.map((step) => {
    const source = step.inputSource
    if (source?.kind === 'mapPoint') {
      const point = source.point
      if (
        entityIds.has(point.id) ||
        (point.sourceEntity != null && entityIds.has(point.sourceEntity.id))
      ) {
        changed = true
        return resetOperationStep(step)
      }
    }
    // Selection-backed (and any other) steps whose result belonged to the
    // removed entity — fully reset so a stale operatorId can't keep the
    // collapsed output badge inflated after unpinning.
    if (step.result && entityIds.has(step.result.entityId)) {
      changed = true
      return resetOperationStep(step)
    }
    return step
  })
  return changed ? next : steps
}

/** Drop selection-backed ops when nothing is pinned anymore. */
export function clearSelectionBackedOperationSteps(steps: OperationStep[]): OperationStep[] {
  let changed = false
  const next = steps.map((step) => {
    if (step.inputSource?.kind !== 'selection') return step
    if (!step.operatorId && !step.result) return step
    changed = true
    return resetOperationStep(step)
  })
  return changed ? next : steps
}

/** Clear selection-backed results whose parent entity no longer exists in data. */
export function clearStaleSelectionOperationResults(
  steps: OperationStep[],
  catalogs: {
    jobs: ReadonlyArray<{ id: string }>
    people: ReadonlyArray<{ id: string }>
    assets: ReadonlyArray<{ id: string }>
    municipalities: ReadonlyArray<{ id: string }>
  },
): OperationStep[] {
  const exists = (category: OperationSelectionCategory, id: string): boolean => {
    switch (category) {
      case 'job':
        return catalogs.jobs.some((j) => j.id === id)
      case 'person':
        return catalogs.people.some((p) => p.id === id)
      case 'asset':
        return catalogs.assets.some((a) => a.id === id)
      case 'municipality':
        return catalogs.municipalities.some((m) => m.id === id)
      default:
        return false
    }
  }

  let changed = false
  const next = steps.map((step) => {
    if (!step.result || step.inputSource?.kind !== 'selection') return step
    if (exists(step.inputSource.category, step.result.entityId)) return step
    changed = true
    return { ...step, result: null }
  })
  return changed ? next : steps
}

/** Synthetic located entity from a prior step’s map result point. */
export function operationEntityFromMapPoint(point: OperationMapPoint): OperationEntity {
  return {
    kind: 'place',
    id: point.id,
    name: point.name,
    lat: point.lat,
    lng: point.lng,
    capabilities: new Set<EntityCapability>(['hasIdentity', 'hasLocation']),
  }
}

export function findStepMapPoint(
  steps: OperationStep[],
  fromStepId: string,
  pointId: string,
): OperationMapPoint | null {
  const step = steps.find((s) => s.id === fromStepId)
  if (!step?.result) return null
  return step.result.mapPoints.find((p) => p.id === pointId) ?? null
}

export const OPERATORS: OperatorDef[] = [
  {
    id: 'eta',
    label: 'ETA',
    description: 'Driving ETA to an address or another located entity (Google Directions).',
    requires: ['hasLocation'],
    args: ['destination'],
  },
  {
    id: 'nearby',
    label: 'Nearby',
    description: 'Find entities or places within a radius of this location.',
    requires: ['hasLocation'],
    args: ['nearbyTarget', 'radiusMiles'],
  },
  {
    id: 'air-miles',
    label: 'Air miles',
    description: 'Great-circle distance and bearing to an address or located entity.',
    requires: ['hasLocation'],
    args: ['destination'],
  },
  {
    id: 'contains',
    label: 'Contains',
    description:
      'List jobs, people, and GPS assets whose coordinates fall inside this boundary.',
    requires: ['hasBoundary'],
    args: [],
  },
  // fuel-planning is implemented but hidden until real Places-backed stops ship.
  {
    id: 'local-license-check',
    label: 'Local license check',
    description:
      'Resolve state / county / city at this location and report license status (or missing data).',
    requires: ['hasLocation'],
    args: [],
  },
  {
    id: 'weather-forecast',
    label: 'Forecast',
    description: '5-day weather forecast at this location (same OpenWeather feed as the wallboard).',
    requires: ['hasLocation'],
    args: [],
  },
  {
    id: 'forecast',
    label: 'Jobs in area',
    description:
      'Job activity inside this municipality for a date range (active, inactive, starting, or ending).',
    requires: ['hasBoundary'],
    args: ['forecastType', 'forecastStartDate', 'forecastEndDate'],
  },
  {
    id: 'time',
    label: 'Time',
    description:
      'Regular, overtime, and total hours from JobTread for a date range (weekly 40 OT).',
    requires: ['hasTimesheet'],
    args: ['timeStartDate', 'timeEndDate'],
  },
  {
    id: 'cert-check',
    label: 'Certification check',
    description:
      'List assigned certifications, status, and expiration for this portal employee.',
    requires: ['hasPortalUser'],
    args: [],
  },
]

export function entityRef(kind: OperationEntityKind, id: string): string {
  return `${kind}:${id}`
}

/** Destination ref for a prior step’s map result point (`stepPoint:<stepId>:<pointId>`). */
export function stepPointRef(fromStepId: string, pointId: string): string {
  return `stepPoint:${fromStepId}:${pointId}`
}

export function parseStepPointRef(
  ref: string,
): { fromStepId: string; pointId: string } | null {
  const trimmed = ref.trim()
  if (!trimmed.startsWith('stepPoint:')) return null
  const rest = trimmed.slice('stepPoint:'.length)
  const idx = rest.indexOf(':')
  if (idx <= 0) return null
  const fromStepId = rest.slice(0, idx)
  const pointId = rest.slice(idx + 1)
  if (!fromStepId || !pointId) return null
  return { fromStepId, pointId }
}

export function parseEntityRef(
  ref: string,
): { kind: OperationEntityKind; id: string } | null {
  const trimmed = ref.trim()
  if (!trimmed) return null
  const idx = trimmed.indexOf(':')
  if (idx <= 0) return null
  const kind = trimmed.slice(0, idx) as OperationEntityKind
  const id = trimmed.slice(idx + 1)
  if (!id) return null
  if (
    kind !== 'job' &&
    kind !== 'person' &&
    kind !== 'asset' &&
    kind !== 'municipality'
  ) {
    return null
  }
  return { kind, id }
}

function hasFiniteLatLng(lat: unknown, lng: unknown): boolean {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lng)
  )
}

export function getEntityCapabilitiesFromFields(fields: {
  id?: string
  name?: string
  lat?: number
  lng?: number
  address?: string
  status?: string
  driver?: string
  fuelLevel?: string
  battery?: string
  censusGeoid?: string
  serviceArea?: GeoJSON.FeatureCollection | null
}): Set<EntityCapability> {
  const caps = new Set<EntityCapability>()
  if (fields.id && fields.name) caps.add('hasIdentity')
  if (hasFiniteLatLng(fields.lat, fields.lng)) caps.add('hasLocation')
  if (fields.address && fields.address.trim()) caps.add('hasAddress')
  if (fields.status) caps.add('hasStatus')
  if (fields.driver && fields.driver.trim()) caps.add('hasAssignee')
  if (fields.fuelLevel && fields.fuelLevel.trim()) caps.add('hasFuelLevel')
  if (fields.battery && fields.battery.trim()) caps.add('hasBattery')
  if (fields.censusGeoid || (fields.serviceArea && fields.serviceArea.features?.length)) {
    caps.add('hasBoundary')
  }
  return caps
}

export function operationEntityFromJob(job: ControlCenterJob): OperationEntity {
  const capabilities = getEntityCapabilitiesFromFields({
    id: job.id,
    name: job.name,
    lat: job.lat,
    lng: job.lng,
    address: job.address,
    status: job.status,
  })
  capabilities.add('hasTimesheet')
  return {
    kind: 'job',
    id: job.id,
    name: job.name,
    lat: job.lat,
    lng: job.lng,
    capabilities,
    job,
  }
}

export function operationEntityFromPerson(person: ControlCenterPerson): OperationEntity | null {
  if (
    !person.clockedIn ||
    person.lat == null ||
    person.lng == null ||
    !Number.isFinite(person.lat) ||
    !Number.isFinite(person.lng)
  ) {
    return null
  }
  const capabilities = getEntityCapabilitiesFromFields({
    id: person.id,
    name: person.name,
    lat: person.lat,
    lng: person.lng,
    address: person.address || undefined,
    status: person.personKind,
  })
  capabilities.add('hasTimesheet')
  if (person.hasPortalUser) capabilities.add('hasPortalUser')
  return {
    kind: 'person',
    id: person.id,
    name: person.name,
    lat: person.lat,
    lng: person.lng,
    capabilities,
    person,
  }
}

/**
 * Identity entity for operators that do not need a map pin (e.g. Time, Certification check).
 * Always has `hasIdentity` + `hasTimesheet`; adds `hasLocation` when the person is clocked in with coords
 * and `hasPortalUser` when a portal User exists.
 */
export function operationEntityFromPersonIdentity(person: ControlCenterPerson): OperationEntity {
  const hasPin =
    person.clockedIn &&
    person.lat != null &&
    person.lng != null &&
    Number.isFinite(person.lat) &&
    Number.isFinite(person.lng)
  const caps = getEntityCapabilitiesFromFields({
    id: person.id,
    name: person.name,
    lat: hasPin ? (person.lat as number) : undefined,
    lng: hasPin ? (person.lng as number) : undefined,
    address: person.address || undefined,
    status: person.personKind,
  })
  caps.add('hasTimesheet')
  if (person.hasPortalUser) caps.add('hasPortalUser')
  return {
    kind: 'person',
    id: person.id,
    name: person.name,
    lat: hasPin ? (person.lat as number) : Number.NaN,
    lng: hasPin ? (person.lng as number) : Number.NaN,
    capabilities: caps,
    person,
  }
}

export function operationEntityFromAsset(asset: ControlCenterAsset): OperationEntity {
  return {
    kind: 'asset',
    id: asset.id,
    name: asset.name,
    lat: asset.lat,
    lng: asset.lng,
    capabilities: getEntityCapabilitiesFromFields({
      id: asset.id,
      name: asset.name,
      lat: asset.lat,
      lng: asset.lng,
      status: asset.status,
      driver: asset.driver,
      fuelLevel: asset.fuelLevel,
      battery: asset.battery,
    }),
    asset,
  }
}

export function operationEntityFromMunicipality(
  municipality: ControlCenterMunicipality,
): OperationEntity {
  return {
    kind: 'municipality',
    id: municipality.id,
    name: municipality.name,
    lat: municipality.lat,
    lng: municipality.lng,
    capabilities: getEntityCapabilitiesFromFields({
      id: municipality.id,
      name: municipality.name,
      lat: municipality.lat,
      lng: municipality.lng,
      censusGeoid: municipality.censusGeoid || undefined,
      status: municipality.level,
    }),
    municipality,
  }
}

export function resolveOperationEntity(ctx: {
  job?: ControlCenterJob | null
  person?: ControlCenterPerson | null
  asset?: ControlCenterAsset | null
  municipality?: ControlCenterMunicipality | null
}): OperationEntity | null {
  if (ctx.job) return operationEntityFromJob(ctx.job)
  if (ctx.person) return operationEntityFromPerson(ctx.person)
  if (ctx.asset) return operationEntityFromAsset(ctx.asset)
  if (ctx.municipality) return operationEntityFromMunicipality(ctx.municipality)
  return null
}

export function resolveStepPrimary(
  step: OperationStep,
  steps: OperationStep[],
  catalogs: {
    job?: ControlCenterJob | null
    person?: ControlCenterPerson | null
    asset?: ControlCenterAsset | null
    municipality?: ControlCenterMunicipality | null
  },
): OperationEntity | null {
  const source = step.inputSource
  if (!source) return null
  if (source.kind === 'mapPoint') {
    return operationEntityFromMapPoint(source.point)
  }
  if (source.kind === 'priorPoint') {
    const point = findStepMapPoint(steps, source.fromStepId, source.pointId)
    return point ? operationEntityFromMapPoint(point) : null
  }
  const category = source.category
  return resolveOperationEntity({
    job: category === 'job' ? catalogs.job : null,
    person: category === 'person' ? catalogs.person : null,
    asset: category === 'asset' ? catalogs.asset : null,
    municipality: category === 'municipality' ? catalogs.municipality : null,
  })
}

export function operatorLabel(operatorId: OperatorId | ''): string {
  if (!operatorId) return ''
  return OPERATORS.find((op) => op.id === operatorId)?.label ?? operatorId
}

function ensureSentence(text: string): string {
  const trimmed = text.trim()
  if (!trimmed) return ''
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`
}

function rowValue(result: OperationResult, label: string): string | null {
  const row = result.rows.find((r) => r.label === label)
  const value = row?.value?.trim()
  return value ? value : null
}

function nearbyTargetShareLabel(target: string): string {
  const meta = NEARBY_TARGET_OPTIONS.find((o) => o.id === target)
  if (meta?.label) return meta.label.toLowerCase()
  return target.replace(/_/g, ' ')
}

/** Point chosen from this step’s results, inferred from the next step’s input. */
function selectedResultPoint(
  step: OperationStep,
  nextStep: OperationStep | undefined,
  steps: OperationStep[],
): OperationMapPoint | null {
  if (!nextStep?.inputSource || !step.result) return null
  const source = nextStep.inputSource
  if (source.kind === 'priorPoint' && source.fromStepId === step.id) {
    return findStepMapPoint(steps, source.fromStepId, source.pointId)
  }
  if (source.kind === 'mapPoint') {
    return (
      step.result.mapPoints.find((point) => point.id === source.point.id) ?? null
    )
  }
  return null
}

function formatPointShareLabel(point: OperationMapPoint): string {
  return point.subtitle ? `${point.name} (${point.subtitle})` : point.name
}

function formatStepShareBlock(
  step: OperationStep,
  stepNumber: number,
  nextStep: OperationStep | undefined,
  steps: OperationStep[],
): string {
  const result = step.result
  if (!result) return ''

  const opName = operatorLabel(step.operatorId) || result.title || 'Operation'
  const subject = result.entityName || 'the selected location'
  const lines: string[] = [`${stepNumber}. ${opName} from ${subject}`]

  const inputs = step.inputs
  let action = ''
  switch (step.operatorId) {
    case 'nearby': {
      const target = nearbyTargetShareLabel(String(inputs.nearbyTarget || 'targets'))
      const radius = Math.max(1, inputs.radiusMiles || 25)
      action = `Searched for ${target} within ${radius} mi of ${subject}.`
      break
    }
    case 'eta': {
      const dest =
        rowValue(result, 'Destination') ||
        result.mapPoints[0]?.name ||
        'the destination'
      action = `Driving route from ${subject} to ${dest}.`
      break
    }
    case 'air-miles': {
      const dest =
        rowValue(result, 'Destination') ||
        result.mapPoints[0]?.name ||
        'the destination'
      action = `Measured air miles from ${subject} to ${dest}.`
      break
    }
    case 'fuel-planning': {
      const range = Math.max(10, inputs.rangeMiles || 80)
      const dest = rowValue(result, 'Destination')
      action = dest && dest !== 'None (search area)'
        ? `Planned fuel stops within about ${range} mi along the route to ${dest}.`
        : `Looked for fuel stops within about ${range} mi of ${subject}.`
      break
    }
    case 'contains':
      action = `Listed jobs, people, and assets inside ${subject}.`
      break
    case 'forecast': {
      const type = rowValue(result, 'Type') || inputs.forecastType
      const range = rowValue(result, 'Range')
      action = range
        ? `Checked ${String(type).toLowerCase()} jobs in ${subject} for ${range}.`
        : `Checked ${String(type).toLowerCase()} jobs in ${subject}.`
      break
    }
    case 'weather-forecast': {
      const today = result.rows.find((r) => r.label !== 'Location' && r.label !== 'Coordinates')
      action = today
        ? `Pulled the 5-day weather forecast at ${subject} (${today.value}).`
        : `Pulled the 5-day weather forecast at ${subject}.`
      break
    }
    case 'local-license-check':
      action = `Checked local license coverage at ${subject}.`
      break
    case 'time': {
      const range = rowValue(result, 'Range')
      action = range
        ? `Pulled timesheet hours for ${subject} (${range}).`
        : `Pulled timesheet hours for ${subject}.`
      break
    }
    case 'cert-check': {
      const count = result.table?.rows.length ?? 0
      action =
        count === 0
          ? `No certifications assigned for ${subject}.`
          : `Listed ${count} certification${count === 1 ? '' : 's'} for ${subject}.`
      break
    }
    default:
      action = ensureSentence(result.summary || `Ran ${opName} for ${subject}.`)
      break
  }
  if (action) lines.push(`   ${ensureSentence(action)}`)

  const selected = selectedResultPoint(step, nextStep, steps)
  if (selected) {
    lines.push(`   Selected: ${formatPointShareLabel(selected)}.`)
  } else if (step.operatorId === 'eta') {
    const eta = rowValue(result, 'ETA')
    const distance = rowValue(result, 'Drive distance')
    const metric = [distance, eta ? `about ${eta}` : null].filter(Boolean).join(' · ')
    if (metric) lines.push(`   Result: ${metric}.`)
  } else if (step.operatorId === 'air-miles') {
    const miles = rowValue(result, 'Air miles')
    const bearing = rowValue(result, 'Bearing')
    const metric = [miles, bearing ? `bearing ${bearing}` : null].filter(Boolean).join(' · ')
    if (metric) lines.push(`   Result: ${metric}.`)
  } else if (result.mapPoints.length === 1) {
    // Single mapped result with no following selection — name that one.
    lines.push(`   Selected: ${formatPointShareLabel(result.mapPoints[0])}.`)
  }

  return lines.join('\n')
}

/**
 * Plain-English share text for steps 1..N through `throughStepId` (inclusive).
 * Returns null when none of those steps have a result yet.
 */
export function formatOperationStepsShareText(
  steps: OperationStep[],
  throughStepId: string,
): string | null {
  const throughIndex = steps.findIndex((step) => step.id === throughStepId)
  if (throughIndex < 0) return null

  const slice = steps.slice(0, throughIndex + 1)
  const withResults = slice
    .map((step, index) => ({ step, stepNumber: index + 1, index }))
    .filter(({ step }) => Boolean(step.result))

  if (withResults.length === 0) return null

  const blocks = withResults
    .map(({ step, stepNumber, index }) =>
      formatStepShareBlock(step, stepNumber, slice[index + 1], steps),
    )
    .filter(Boolean)

  if (blocks.length === 0) return null

  if (blocks.length === 1) return blocks[0]

  const title = `Control Center — steps 1–${withResults[withResults.length - 1].stepNumber}`
  return `${title}\n\n${blocks.join('\n\n')}`
}

export function getAvailableOperators(
  capabilities: Set<EntityCapability> | EntityCapability[],
  options?: {
    enabledOperators?: Partial<Record<OperatorId, boolean>>
  },
): OperatorDef[] {
  const caps = capabilities instanceof Set ? capabilities : new Set(capabilities)
  const enabledOperators = options?.enabledOperators
  return OPERATORS.filter((op) => {
    if (enabledOperators?.[op.id] === false) return false
    return op.requires.every((req) => caps.has(req))
  })
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180
}

function toDeg(rad: number): number {
  return (rad * 180) / Math.PI
}

/** Approximate great-circle distance in miles */
export function milesBetween(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 3958.8
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Initial bearing degrees clockwise from north */
export function bearingDegrees(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const dLng = toRad(b.lng - a.lng)
  const y = Math.sin(dLng) * Math.cos(lat2)
  const x =
    Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng)
  return (toDeg(Math.atan2(y, x)) + 360) % 360
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t
}

function offsetPoint(
  origin: { lat: number; lng: number },
  bearingDeg: number,
  distanceMiles: number,
): { lat: number; lng: number } {
  const R = 3958.8
  const br = toRad(bearingDeg)
  const lat1 = toRad(origin.lat)
  const lng1 = toRad(origin.lng)
  const ang = distanceMiles / R
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(ang) + Math.cos(lat1) * Math.sin(ang) * Math.cos(br),
  )
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(br) * Math.sin(ang) * Math.cos(lat1),
      Math.cos(ang) - Math.sin(lat1) * Math.sin(lat2),
    )
  return { lat: (lat2 * 180) / Math.PI, lng: (lng2 * 180) / Math.PI }
}

function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0]
    const yi = ring[i][1]
    const xj = ring[j][0]
    const yj = ring[j][1]
    if (yi === yj) continue
    const intersect = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

function pointInPolygonCoords(lng: number, lat: number, coords: number[][][]): boolean {
  if (!coords.length) return false
  if (!pointInRing(lng, lat, coords[0])) return false
  for (let i = 1; i < coords.length; i += 1) {
    if (pointInRing(lng, lat, coords[i])) return false
  }
  return true
}

export function pointInGeoJson(
  point: { lat: number; lng: number },
  collection: GeoJSON.FeatureCollection,
): boolean {
  for (const feature of collection.features) {
    const g = feature.geometry
    if (!g) continue
    if (g.type === 'Polygon') {
      if (pointInPolygonCoords(point.lng, point.lat, g.coordinates as number[][][])) return true
    } else if (g.type === 'MultiPolygon') {
      for (const poly of g.coordinates as number[][][][]) {
        if (pointInPolygonCoords(point.lng, point.lat, poly)) return true
      }
    }
  }
  return false
}

const DEMO_FUEL_BRANDS = ['Pilot', 'Love’s', 'Sinclair', 'Shell', 'Kum & Go'] as const

function demoFuelStopsAround(
  origin: { lat: number; lng: number },
  rangeMiles: number,
): OperationMapPoint[] {
  const bearings = [25, 110, 210, 300]
  const count = Math.min(4, Math.max(2, Math.round(rangeMiles / 35)))
  return bearings.slice(0, count).map((bearing, i) => {
    const dist = Math.max(8, rangeMiles * (0.35 + i * 0.12))
    const pt = offsetPoint(origin, bearing, dist)
    return {
      id: `fuel-stop-${i + 1}`,
      name: `${DEMO_FUEL_BRANDS[i % DEMO_FUEL_BRANDS.length]} #${100 + i}`,
      subtitle: `~${Math.round(dist)} mi · diesel`,
      lat: pt.lat,
      lng: pt.lng,
      color: '#0d9488',
    }
  })
}

function demoFuelStopsAlongRoute(
  origin: { lat: number; lng: number },
  destination: { lat: number; lng: number },
  rangeMiles: number,
): { stops: OperationMapPoint[]; route: Array<[number, number]> } {
  const totalMiles = milesBetween(origin, destination)
  const route: Array<[number, number]> = [
    [origin.lat, origin.lng],
    [destination.lat, destination.lng],
  ]
  const stopCount = Math.max(1, Math.min(3, Math.ceil(totalMiles / Math.max(rangeMiles * 0.7, 1)) - 1))
  const stops: OperationMapPoint[] = []
  for (let i = 1; i <= stopCount; i += 1) {
    const t = i / (stopCount + 1)
    const lat = lerp(origin.lat, destination.lat, t)
    const lng = lerp(origin.lng, destination.lng, t)
    const nudge = offsetPoint({ lat, lng }, 90, 2 + i)
    stops.push({
      id: `fuel-route-${i}`,
      name: `${DEMO_FUEL_BRANDS[i % DEMO_FUEL_BRANDS.length]} #${200 + i}`,
      subtitle: `~${Math.round(totalMiles * t)} mi along route`,
      lat: nudge.lat,
      lng: nudge.lng,
      color: '#0d9488',
    })
  }
  return { stops, route }
}

/** Decode Google encoded polyline → Leaflet [lat, lng] path */
export function decodeGooglePolyline(encoded: string): Array<[number, number]> {
  if (!encoded) return []
  const coordinates: Array<[number, number]> = []
  let index = 0
  let lat = 0
  let lng = 0

  while (index < encoded.length) {
    let shift = 0
    let result = 0
    let byte: number
    do {
      byte = encoded.charCodeAt(index++) - 63
      result |= (byte & 0x1f) << shift
      shift += 5
    } while (byte >= 0x20)
    const deltaLat = result & 1 ? ~(result >> 1) : result >> 1
    lat += deltaLat

    shift = 0
    result = 0
    do {
      byte = encoded.charCodeAt(index++) - 63
      result |= (byte & 0x1f) << shift
      shift += 5
    } while (byte >= 0x20)
    const deltaLng = result & 1 ? ~(result >> 1) : result >> 1
    lng += deltaLng

    coordinates.push([lat / 1e5, lng / 1e5])
  }

  return coordinates
}

export type DestinationPoint = {
  label: string
  lat: number
  lng: number
  source: 'entity' | 'address' | 'stepPoint'
  /** When the destination is (or resolves to) a portal entity, prefer that live marker. */
  sourceEntity?: {
    category: 'job' | 'person' | 'asset' | 'municipality'
    id: string
  }
}

/** Stable map point for an existing portal entity — id matches the live marker. */
export function mapPointForLocatedEntity(args: {
  category: 'job' | 'person' | 'asset' | 'municipality'
  id: string
  name: string
  lat: number
  lng: number
  color: string
  subtitle?: string
}): OperationMapPoint {
  return {
    id: args.id,
    name: args.name,
    subtitle: args.subtitle,
    lat: args.lat,
    lng: args.lng,
    color: args.color,
    sourceEntity: { category: args.category, id: args.id },
  }
}

/** Destination pin: reuse the entity marker when possible, otherwise a synthetic place pin. */
export function mapPointForDestination(
  dest: DestinationPoint,
  opts?: { subtitle?: string; color?: string },
): OperationMapPoint {
  const color = opts?.color ?? '#7c3aed'
  const subtitle = opts?.subtitle
  if (dest.sourceEntity) {
    return mapPointForLocatedEntity({
      category: dest.sourceEntity.category,
      id: dest.sourceEntity.id,
      name: dest.label,
      lat: dest.lat,
      lng: dest.lng,
      color,
      subtitle,
    })
  }
  return {
    id: `dest-${dest.label}`,
    name: dest.label,
    subtitle: subtitle ?? 'Destination',
    lat: dest.lat,
    lng: dest.lng,
    color,
  }
}

/** For ETA: destination may be lat/lng or a free-text address (server geocodes via Directions). */
export type EtaDestination =
  | { kind: 'coords'; label: string; lat: number; lng: number }
  | { kind: 'address'; label: string; address: string }

export function resolveEtaDestination(
  inputs: OperationInputs,
  catalogs: {
    jobs: ControlCenterJob[]
    people: ControlCenterPerson[]
    assets: ControlCenterAsset[]
    municipalities: ControlCenterMunicipality[]
  },
  steps: OperationStep[] = [],
): EtaDestination | null {
  if (inputs.destinationMode === 'address') {
    const text = inputs.destinationAddress.trim()
    if (!text) return null
    const coordMatch = text.match(/^\s*(-?\d+(\.\d+)?)\s*,\s*(-?\d+(\.\d+)?)\s*$/)
    if (coordMatch) {
      const lat = Number(coordMatch[1])
      const lng = Number(coordMatch[3])
      if (hasFiniteLatLng(lat, lng)) {
        return { kind: 'coords', label: text, lat, lng }
      }
    }
    return { kind: 'address', label: text, address: text }
  }

  const point = resolveDestinationPoint(inputs, catalogs, steps)
  if (!point) return null
  return { kind: 'coords', label: point.label, lat: point.lat, lng: point.lng }
}

export type ControlCenterEtaResult = {
  duration: string
  distance: string
  polyline: string
  startAddress: string
  endAddress: string
  steps?: Array<{ instruction: string; distance?: string; duration?: string }>
  mode?: string
}

export async function runEtaOperation(args: {
  primary: OperationEntity
  inputs: OperationInputs
  jobs: ControlCenterJob[]
  people: ControlCenterPerson[]
  assets: ControlCenterAsset[]
  municipalities: ControlCenterMunicipality[]
  /** Prior / sibling steps for resolving stepPoint destinations. */
  steps?: OperationStep[]
}): Promise<OperationResult> {
  const { primary, inputs, jobs, people, assets, municipalities, steps = [] } = args
  const ranAt = Date.now()
  const dest = resolveEtaDestination(
    inputs,
    { jobs, people, assets, municipalities },
    steps,
  )

  if (!dest) {
    return {
      operatorId: 'eta',
      title: 'ETA',
      summary:
        inputs.destinationMode === 'address'
          ? 'Enter a destination address (or lat,lng).'
          : 'Choose a destination entity.',
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [{ label: 'Origin', value: primary.name }],
      mapPoints: [],
      routeLine: null,
    }
  }

  const origin = `${primary.lat},${primary.lng}`
  const destination =
    dest.kind === 'coords' ? `${dest.lat},${dest.lng}` : dest.address

  const data = await executeCapability<ControlCenterEtaResult>({
    app: 'controlCenter',
    capability: 'controlCenter.eta',
    input: { origin, destination, mode: 'driving' },
  })

  const routeLine = decodeGooglePolyline(data.polyline || '')
  const destLat =
    dest.kind === 'coords'
      ? dest.lat
      : routeLine.length > 0
        ? routeLine[routeLine.length - 1][0]
        : primary.lat
  const destLng =
    dest.kind === 'coords'
      ? dest.lng
      : routeLine.length > 0
        ? routeLine[routeLine.length - 1][1]
        : primary.lng
  const destLabel = data.endAddress || dest.label
  const catalogs = { jobs, people, assets, municipalities }
  const resolvedDest =
    inputs.destinationMode === 'entity'
      ? resolveDestinationPoint(inputs, catalogs, steps)
      : null
  const destPoint: DestinationPoint = resolvedDest ?? {
    label: destLabel,
    lat: dest.kind === 'coords' ? dest.lat : destLat,
    lng: dest.kind === 'coords' ? dest.lng : destLng,
    source: 'address',
  }
  const etaSubtitle = data.duration
    ? `ETA ${data.duration}${
        destPoint.sourceEntity && data.endAddress && data.endAddress !== destPoint.label
          ? ` · ${data.endAddress}`
          : ''
      }`
    : destPoint.sourceEntity
      ? data.endAddress && data.endAddress !== destPoint.label
        ? data.endAddress
        : 'Destination'
      : 'Destination'

  return {
    operatorId: 'eta',
    title: 'ETA',
    summary: `ETA ${data.duration || '—'} (${data.distance || '—'}) to ${destLabel}.`,
    entityId: primary.id,
    entityName: primary.name,
    ranAt,
    rows: [
      { label: 'Origin', value: data.startAddress || primary.name },
      { label: 'Destination', value: destLabel },
      { label: 'Drive distance', value: data.distance || '—' },
      { label: 'ETA', value: data.duration || '—' },
      { label: 'Mode', value: data.mode || 'driving' },
    ],
    // Prefer the live entity pin (employee/job/…) over a Google-snapped address marker.
    mapPoints: [
      mapPointForDestination(
        destPoint.sourceEntity
          ? destPoint
          : { ...destPoint, lat: destLat, lng: destLng, label: destLabel },
        { subtitle: etaSubtitle },
      ),
    ],
    routeLine:
      routeLine.length >= 2
        ? routeLine
        : [
            [primary.lat, primary.lng],
            [destLat, destLng],
          ],
    mapsDirectionsUrl: googleMapsDirectionsUrl({
      originLat: primary.lat,
      originLng: primary.lng,
      destLat,
      destLng,
    }),
  }
}

export async function runWeatherForecastOperation(args: {
  primary: OperationEntity
}): Promise<OperationResult> {
  const { primary } = args
  const ranAt = Date.now()

  if (!Number.isFinite(primary.lat) || !Number.isFinite(primary.lng)) {
    return {
      operatorId: 'weather-forecast',
      title: 'Forecast',
      summary: 'Selected entity has no usable coordinates.',
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [],
      mapPoints: [],
      routeLine: null,
    }
  }

  try {
    const payload = await executeCapability<{ data?: unknown }>({
      app: 'controlCenter',
      capability: 'weather.forecastByCoords',
      input: { lat: primary.lat, lon: primary.lng },
    })
    const { days } = aggregateForecast(payload?.data, 5)

    if (!days.length) {
      return {
        operatorId: 'weather-forecast',
        title: 'Forecast',
        summary: 'Forecast unavailable for this location.',
        entityId: primary.id,
        entityName: primary.name,
        ranAt,
        rows: [
          { label: 'Location', value: primary.name },
          {
            label: 'Coordinates',
            value: `${primary.lat.toFixed(4)}, ${primary.lng.toFixed(4)}`,
          },
        ],
        mapPoints: [],
        routeLine: null,
      }
    }

    const today = days[0]!
    const weatherSubtitle = `${today.maxF}° / ${today.minF}° · ${today.main}`
    const mapPoints: OperationMapPoint[] =
      primary.kind === 'job' ||
      primary.kind === 'person' ||
      primary.kind === 'asset' ||
      primary.kind === 'municipality'
        ? [
            mapPointForLocatedEntity({
              category: primary.kind,
              id: primary.id,
              name: primary.name,
              lat: primary.lat,
              lng: primary.lng,
              color: '#0ea5e9',
              subtitle: weatherSubtitle,
            }),
          ]
        : [
            {
              id: `weather-${primary.id}`,
              name: primary.name,
              subtitle: weatherSubtitle,
              lat: primary.lat,
              lng: primary.lng,
              color: '#0ea5e9',
            },
          ]

    return {
      operatorId: 'weather-forecast',
      title: 'Forecast',
      summary: `${days.length}-day forecast at ${primary.name}: ${weatherSubtitle}.`,
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [
        { label: 'Location', value: primary.name },
        {
          label: 'Coordinates',
          value: `${primary.lat.toFixed(4)}, ${primary.lng.toFixed(4)}`,
        },
        ...days.map((day) => ({
          label: day.weekdayShort,
          value: `${day.maxF}° / ${day.minF}° · ${day.main}`,
        })),
      ],
      mapPoints,
      routeLine: null,
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Weather unavailable'
    return {
      operatorId: 'weather-forecast',
      title: 'Forecast',
      summary: message,
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [
        { label: 'Location', value: primary.name },
        {
          label: 'Coordinates',
          value: `${primary.lat.toFixed(4)}, ${primary.lng.toFixed(4)}`,
        },
        { label: 'Status', value: message },
      ],
      mapPoints: [],
      routeLine: null,
    }
  }
}

type TimeHoursCapabilityRow = {
  id: string
  name: string
  regular: string
  overtime: string
  total: string
}

type TimeHoursCapabilityResult = {
  kind: 'job' | 'person'
  entityId: string
  entityName: string
  startDate: string
  endDate: string
  rowLabel: 'employee' | 'job'
  rows: TimeHoursCapabilityRow[]
  totals: { regular: string; overtime: string; total: string }
  fetchedAt?: string
}

/** JobTread timesheet hours — regular / OT / total via controlCenter.timeHours. */
export async function runTimeOperation(args: {
  primary: OperationEntity
  inputs: OperationInputs
}): Promise<OperationResult> {
  const { primary, inputs } = args
  const ranAt = Date.now()
  const startDate = String(inputs.timeStartDate || '').trim()
  const endDate = String(inputs.timeEndDate || '').trim()

  if (primary.kind !== 'job' && primary.kind !== 'person') {
    return {
      operatorId: 'time',
      title: 'Time',
      summary: 'Time is available for jobs and people only.',
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [],
      mapPoints: [],
      routeLine: null,
    }
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
    return {
      operatorId: 'time',
      title: 'Time',
      summary: 'Choose a start and end date.',
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [],
      mapPoints: [],
      routeLine: null,
    }
  }

  try {
    const data = await executeCapability<TimeHoursCapabilityResult>({
      app: 'controlCenter',
      capability: 'controlCenter.timeHours',
      input: {
        kind: primary.kind,
        id: primary.id,
        startDate,
        endDate,
        entityName: primary.name,
      },
    })

    const nameCol = data.rowLabel === 'employee' ? 'Employee' : 'Job'
    const rowEntityCategory = data.rowLabel === 'employee' ? 'person' : 'job'
    const tableRows = (Array.isArray(data.rows) ? data.rows : []).map((r) => ({
      id: r.id,
      name: r.name,
      regular: r.regular,
      overtime: r.overtime,
      total: r.total,
    }))
    const totals = data.totals || { regular: '0.00', overtime: '0.00', total: '0.00' }
    const rangeLabel = `${data.startDate || startDate} → ${data.endDate || endDate}`
    const rowCount = tableRows.length
    const summary =
      rowCount === 0
        ? `No time entries for ${primary.name} in ${rangeLabel}.`
        : `${totals.total}h total for ${primary.name} (${rangeLabel}; ${rowCount} ${
            data.rowLabel === 'employee' ? 'employee' : 'job'
          }${rowCount === 1 ? '' : 's'}).`

    return {
      operatorId: 'time',
      title: 'Time',
      summary,
      entityId: primary.id,
      entityName: data.entityName || primary.name,
      ranAt,
      rows: [
        { label: 'Range', value: rangeLabel },
      ],
      mapPoints: [],
      routeLine: null,
      table: {
        columns: [
          { key: 'name', label: nameCol, align: 'left' },
          { key: 'regular', label: 'Regular', align: 'right' },
          { key: 'overtime', label: 'OT', align: 'right' },
          { key: 'total', label: 'Total', align: 'right' },
        ],
        rows: tableRows,
        rowEntityCategory,
        footer: {
          name: 'Total',
          regular: totals.regular,
          overtime: totals.overtime,
          total: totals.total,
        },
      },
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Timesheet unavailable'
    return {
      operatorId: 'time',
      title: 'Time',
      summary: message,
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [
        { label: 'Range', value: `${startDate} → ${endDate}` },
        { label: 'Status', value: message },
      ],
      mapPoints: [],
      routeLine: null,
    }
  }
}

export type CertCheckCapabilityCert = {
  type: string
  label?: string
  status: string
  expirationDate: string | Date | null
  hasFile?: boolean
}

export type CertCheckCapabilityResult = {
  employeeId: string
  certs: CertCheckCapabilityCert[]
}

function certStatusLabel(status: string): string {
  if (status === 'good') return 'Complete'
  if (status === 'bad') return 'Expired'
  if (status === 'inProgress') return 'In Progress'
  return status.trim() || '—'
}

function formatCertExpiration(value: unknown): string {
  if (value == null || value === '') return '—'
  const raw = String(value).trim()
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (dateOnly) return `${dateOnly[2]}/${dateOnly[3]}/${dateOnly[1]}`
  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) return raw
  return parsed.toLocaleDateString()
}

function certDisplayName(type: string, label?: string | null): string {
  return certDisplayLabel({ type, label }) || '—'
}

/** Assigned portal certifications — via controlCenter.certCheck. */
export async function runCertCheckOperation(args: {
  primary: OperationEntity
}): Promise<OperationResult> {
  const { primary } = args
  const ranAt = Date.now()
  const employeeId = String(primary.person?.employeeId || '').trim()

  if (primary.kind !== 'person' || !employeeId || !primary.person?.hasPortalUser) {
    return {
      operatorId: 'cert-check',
      title: 'Certification check',
      summary: 'Certification check is available for portal employees only.',
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [],
      mapPoints: [],
      routeLine: null,
    }
  }

  try {
    const data = await executeCapability<CertCheckCapabilityResult>({
      app: 'controlCenter',
      capability: 'controlCenter.certCheck',
      input: { employeeId },
    })
    const certs = Array.isArray(data.certs) ? data.certs : []
    const tableRows = certs.map((c) => {
      const type = String(c.type || '').trim()
      const hasFile = Boolean(c.hasFile)
      return {
        id: type,
        name: certDisplayName(type, c.label),
        status: certStatusLabel(String(c.status || '')),
        expiration: formatCertExpiration(c.expirationDate),
        file: hasFile ? 'View' : '',
        certType: type,
        portalEmployeeId: employeeId,
        hasFile: hasFile ? '1' : '',
      }
    })
    const count = tableRows.length
    const summary =
      count === 0
        ? `No certifications assigned for ${primary.name}.`
        : `${count} certification${count === 1 ? '' : 's'} for ${primary.name}.`

    return {
      operatorId: 'cert-check',
      title: 'Certification check',
      summary,
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [],
      mapPoints: [],
      routeLine: null,
      table: {
        columns: [
          { key: 'name', label: 'Certification', align: 'left' },
          { key: 'status', label: 'Status', align: 'left' },
          { key: 'expiration', label: 'Expiration', align: 'left' },
          { key: 'file', label: 'File', align: 'left' },
        ],
        rows: tableRows,
        emptyMessage: 'No certifications assigned',
      },
    }
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Certifications unavailable'
    return {
      operatorId: 'cert-check',
      title: 'Certification check',
      summary: message,
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [{ label: 'Status', value: message }],
      mapPoints: [],
      routeLine: null,
    }
  }
}

export type ControlCenterNearbyPlace = {
  placeId: string
  name: string
  address: string
  rating: number | null
  openNow: boolean | null
  lat: number
  lng: number
  distanceKm: number
  distanceMiles: number
}

export type ControlCenterNearbyPlacesResult = {
  origin: { lat: number; lng: number }
  placeType: string
  radiusMeters: number
  radiusMiles: number
  places: ControlCenterNearbyPlace[]
}

const PLACES_MARKER_COLOR = '#ea580c'

/** Nearby Google Places within radius — uses controlCenter.nearbyPlaces capability. */
export async function runNearbyPlacesOperation(args: {
  primary: OperationEntity
  inputs: OperationInputs
}): Promise<OperationResult> {
  const { primary, inputs } = args
  const ranAt = Date.now()
  const target = String(inputs.nearbyTarget || '').trim()
  const targetMeta = NEARBY_TARGET_OPTIONS.find((o) => o.id === target)
  const targetLabel = targetMeta?.label || target || '—'
  const radiusMiles = Math.max(1, Math.min(200, inputs.radiusMiles || 25))

  if (!target || isNearbyLayerTarget(target)) {
    return {
      operatorId: 'nearby',
      title: 'Nearby',
      summary: 'Choose an establishment type (e.g. gas stations) to search Google Places.',
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [{ label: 'Origin', value: primary.name }],
      mapPoints: [],
      routeLine: null,
    }
  }

  const data = await executeCapability<ControlCenterNearbyPlacesResult>({
    app: 'controlCenter',
    capability: 'controlCenter.nearbyPlaces',
    input: {
      lat: primary.lat,
      lng: primary.lng,
      placeType: target,
      radiusMiles,
      maxResults: 20,
    },
  })

  const places = Array.isArray(data.places) ? data.places : []
  const effectiveRadius = data.radiusMiles || radiusMiles

  return {
    operatorId: 'nearby',
    title: 'Nearby',
    summary:
      places.length === 0
        ? `No ${targetLabel.toLowerCase()} within ${effectiveRadius} mi of ${primary.name}.`
        : `${places.length} ${targetLabel.toLowerCase()} within ${effectiveRadius} mi of ${primary.name}.`,
    entityId: primary.id,
    entityName: primary.name,
    ranAt,
    rows: [
      { label: 'Origin', value: primary.name },
      { label: 'Target', value: targetLabel },
      { label: 'Place type', value: data.placeType || target },
      { label: 'Radius', value: `${effectiveRadius} mi` },
      { label: 'Matches', value: String(places.length) },
    ],
    mapPoints: places.map((p, i) => ({
      id: `place-${p.placeId || i}`,
      name: p.name,
      subtitle: `~${p.distanceMiles.toFixed(1)} mi${p.rating != null ? ` · ★ ${p.rating}` : ''}`,
      lat: p.lat,
      lng: p.lng,
      color: PLACES_MARKER_COLOR,
    })),
    routeLine: null,
  }
}

export function resolveDestinationPoint(
  inputs: OperationInputs,
  catalogs: {
    jobs: ControlCenterJob[]
    people: ControlCenterPerson[]
    assets: ControlCenterAsset[]
    municipalities: ControlCenterMunicipality[]
  },
  steps: OperationStep[] = [],
): DestinationPoint | null {
  if (inputs.destinationMode === 'address') {
    const text = inputs.destinationAddress.trim()
    if (!text) return null
    // Demo geocode: not available client-side — require entity mode for runnable demos,
    // but allow a synthetic pin only when user typed coordinates "lat,lng".
    const coordMatch = text.match(/^\s*(-?\d+(\.\d+)?)\s*,\s*(-?\d+(\.\d+)?)\s*$/)
    if (coordMatch) {
      const lat = Number(coordMatch[1])
      const lng = Number(coordMatch[3])
      if (hasFiniteLatLng(lat, lng)) {
        return { label: text, lat, lng, source: 'address' }
      }
    }
    return null
  }

  const stepPoint = parseStepPointRef(inputs.destinationEntityRef)
  if (stepPoint) {
    const point = findStepMapPoint(steps, stepPoint.fromStepId, stepPoint.pointId)
    if (!point || !hasFiniteLatLng(point.lat, point.lng)) return null
    return {
      label: point.name,
      lat: point.lat,
      lng: point.lng,
      source: 'stepPoint',
      sourceEntity: point.sourceEntity,
    }
  }

  // Allow lat,lng entity refs (e.g. "My location" pin as destination).
  const entityCoordMatch = inputs.destinationEntityRef
    .trim()
    .match(/^\s*(-?\d+(\.\d+)?)\s*,\s*(-?\d+(\.\d+)?)\s*$/)
  if (entityCoordMatch) {
    const lat = Number(entityCoordMatch[1])
    const lng = Number(entityCoordMatch[3])
    if (hasFiniteLatLng(lat, lng)) {
      return { label: 'My location', lat, lng, source: 'address' }
    }
  }

  const parsed = parseEntityRef(inputs.destinationEntityRef)
  if (!parsed) return null
  if (parsed.kind === 'job') {
    const job = catalogs.jobs.find((j) => j.id === parsed.id)
    return job
      ? {
          label: job.name,
          lat: job.lat,
          lng: job.lng,
          source: 'entity',
          sourceEntity: { category: 'job', id: job.id },
        }
      : null
  }
  if (parsed.kind === 'person') {
    const person = catalogs.people.find((p) => p.id === parsed.id)
    if (
      !person ||
      person.lat == null ||
      person.lng == null ||
      !Number.isFinite(person.lat) ||
      !Number.isFinite(person.lng)
    ) {
      return null
    }
    return {
      label: person.name,
      lat: person.lat,
      lng: person.lng,
      source: 'entity',
      sourceEntity: { category: 'person', id: person.id },
    }
  }
  if (parsed.kind === 'asset') {
    const asset = catalogs.assets.find((a) => a.id === parsed.id)
    return asset
      ? {
          label: asset.name,
          lat: asset.lat,
          lng: asset.lng,
          source: 'entity',
          sourceEntity: { category: 'asset', id: asset.id },
        }
      : null
  }
  if (parsed.kind === 'municipality') {
    const muni = catalogs.municipalities.find((m) => m.id === parsed.id)
    return muni
      ? {
          label: muni.name,
          lat: muni.lat,
          lng: muni.lng,
          source: 'entity',
          sourceEntity: { category: 'municipality', id: muni.id },
        }
      : null
  }
  return null
}

export function runOperation(args: {
  operatorId: OperatorId
  primary: OperationEntity
  inputs: OperationInputs
  jobs: ControlCenterJob[]
  people: ControlCenterPerson[]
  assets: ControlCenterAsset[]
  municipalities: ControlCenterMunicipality[]
  /** Optional live municipality boundary for contains checks */
  municipalityBoundary?: GeoJSON.FeatureCollection | null
  /** Prior steps for resolving stepPoint destinations. */
  steps?: OperationStep[]
}): OperationResult | null {
  const {
    operatorId,
    primary,
    inputs,
    jobs,
    people,
    assets,
    municipalities,
    municipalityBoundary = null,
    steps = [],
  } = args
  const ranAt = Date.now()
  const catalogs = { jobs, people, assets, municipalities }

  if (operatorId === 'air-miles') {
    const dest = resolveDestinationPoint(inputs, catalogs, steps)
    if (!dest) {
      return {
        operatorId,
        title: 'Air miles',
        summary:
          inputs.destinationMode === 'address'
            ? 'Enter coordinates as lat,lng, or switch to an entity destination.'
            : 'Choose a destination entity.',
        entityId: primary.id,
        entityName: primary.name,
        ranAt,
        rows: [{ label: 'Origin', value: primary.name }],
        mapPoints: [],
        routeLine: null,
      }
    }
    const miles = milesBetween(primary, dest)
    const bearing = bearingDegrees(primary, dest)
    return {
      operatorId,
      title: 'Air miles',
      summary: `${miles.toFixed(1)} mi from ${primary.name} to ${dest.label}.`,
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [
        { label: 'Origin', value: primary.name },
        { label: 'Destination', value: dest.label },
        { label: 'Air miles', value: `${miles.toFixed(1)} mi` },
        { label: 'Bearing', value: `${bearing.toFixed(0)}°` },
      ],
      mapPoints: [
        mapPointForDestination(dest, {
          subtitle: `${miles.toFixed(1)} mi · ${bearing.toFixed(0)}°`,
        }),
      ],
      routeLine: [
        [primary.lat, primary.lng],
        [dest.lat, dest.lng],
      ],
    }
  }

  if (operatorId === 'eta') {
    return {
      operatorId,
      title: 'ETA',
      summary: 'ETA requires the server Operation Stack — use runEtaOperation.',
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [],
      mapPoints: [],
      routeLine: null,
    }
  }

  if (operatorId === 'weather-forecast') {
    return {
      operatorId,
      title: 'Forecast',
      summary: 'Weather forecast requires the server Operation Stack — use runWeatherForecastOperation.',
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [],
      mapPoints: [],
      routeLine: null,
    }
  }

  if (operatorId === 'time') {
    return {
      operatorId,
      title: 'Time',
      summary: 'Time requires the server Operation Stack — use runTimeOperation.',
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [],
      mapPoints: [],
      routeLine: null,
    }
  }

  if (operatorId === 'cert-check') {
    return {
      operatorId,
      title: 'Certification check',
      summary: 'Certification check requires the server Operation Stack — use runCertCheckOperation.',
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [],
      mapPoints: [],
      routeLine: null,
    }
  }

  if (operatorId === 'nearby') {
    const radiusMiles = Math.max(1, Math.min(200, inputs.radiusMiles || 25))
    const target = String(inputs.nearbyTarget || '').trim()
    const targetMeta = NEARBY_TARGET_OPTIONS.find((o) => o.id === target)
    const targetLabel = targetMeta?.label || target || '—'

    if (!target) {
      return {
        operatorId,
        title: 'Nearby',
        summary: 'Choose a layer or establishment type to search.',
        entityId: primary.id,
        entityName: primary.name,
        ranAt,
        rows: [{ label: 'Origin', value: primary.name }],
        mapPoints: [],
        routeLine: null,
      }
    }

    // Google Places establishment types — server Places search not wired into Control Center yet.
    if (!isNearbyLayerTarget(target)) {
      return {
        operatorId,
        title: 'Nearby',
        summary: `Places search for “${targetLabel}” requires the server Operation Stack (not available in demo).`,
        entityId: primary.id,
        entityName: primary.name,
        ranAt,
        rows: [
          { label: 'Origin', value: primary.name },
          { label: 'Target', value: targetLabel },
          { label: 'Place type', value: target },
          { label: 'Radius', value: `${radiusMiles} mi` },
          { label: 'Status', value: 'Stub — Places API later' },
        ],
        mapPoints: [],
        routeLine: null,
      }
    }

    type Hit = {
      id: string
      category: 'job' | 'person' | 'asset' | 'municipality' | null
      name: string
      subtitle: string
      lat: number
      lng: number
      miles: number
      color: string
    }
    let hits: Hit[] = []

    if (target === 'assets') {
      hits = assets
        .filter((a) => !(primary.kind === 'asset' && a.id === primary.id))
        .map((a) => ({
          id: a.id,
          category: 'asset' as const,
          name: a.name,
          subtitle: a.status,
          lat: a.lat,
          lng: a.lng,
          miles: milesBetween(primary, a),
          color: '#2563eb',
        }))
    } else if (target === 'jobs') {
      hits = jobs
        .filter((j) => !(primary.kind === 'job' && j.id === primary.id))
        .map((j) => ({
          id: j.id,
          category: 'job' as const,
          name: j.name,
          subtitle: j.number ? `#${j.number}` : j.status,
          lat: j.lat,
          lng: j.lng,
          miles: milesBetween(primary, j),
          color: '#d97706',
        }))
    } else if (target === 'people') {
      hits = people
        .filter(
          (p) =>
            !(primary.kind === 'person' && p.id === primary.id) &&
            p.lat != null &&
            p.lng != null &&
            Number.isFinite(p.lat) &&
            Number.isFinite(p.lng),
        )
        .map((p) => ({
          id: p.id,
          category: 'person' as const,
          name: p.name,
          subtitle: p.personKind === 'employee' ? 'Employee' : 'External',
          lat: p.lat as number,
          lng: p.lng as number,
          miles: milesBetween(primary, { lat: p.lat as number, lng: p.lng as number }),
          color: p.personKind === 'employee' ? '#0d9488' : '#c026d3',
        }))
    } else if (target === 'municipalities') {
      hits = municipalities
        .filter((m) => !(primary.kind === 'municipality' && m.id === primary.id))
        .map((m) => ({
          id: m.id,
          category: 'municipality' as const,
          name: m.name,
          subtitle: m.level,
          lat: m.lat,
          lng: m.lng,
          miles: milesBetween(primary, m),
          color: '#64748b',
        }))
    }

    const nearby = hits.filter((h) => h.miles <= radiusMiles).sort((a, b) => a.miles - b.miles)
    return {
      operatorId,
      title: 'Nearby',
      summary:
        nearby.length === 0
          ? `No ${targetLabel.toLowerCase()} within ${radiusMiles} mi of ${primary.name}.`
          : `${nearby.length} ${targetLabel.toLowerCase()} within ${radiusMiles} mi of ${primary.name}.`,
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [
        { label: 'Origin', value: primary.name },
        { label: 'Target', value: targetLabel },
        { label: 'Radius', value: `${radiusMiles} mi` },
        { label: 'Matches', value: String(nearby.length) },
      ],
      mapPoints: nearby.map((h) =>
        h.category
          ? mapPointForLocatedEntity({
              category: h.category,
              id: h.id,
              name: h.name,
              lat: h.lat,
              lng: h.lng,
              color: h.color,
              subtitle: `~${h.miles.toFixed(1)} mi`,
            })
          : {
              id: `nearby-${h.id}`,
              name: h.name,
              subtitle: `~${h.miles.toFixed(1)} mi`,
              lat: h.lat,
              lng: h.lng,
              color: h.color,
            },
      ),
      routeLine: null,
    }
  }

  if (operatorId === 'contains') {
    if (!municipalityBoundary) {
      return {
        operatorId,
        title: 'Contains',
        summary:
          'Boundary geometry is not loaded yet — wait for the outline on the map, then run again.',
        entityId: primary.id,
        entityName: primary.name,
        ranAt,
        rows: [{ label: 'Area', value: primary.name }],
        mapPoints: [],
        routeLine: null,
      }
    }

    const boundary = municipalityBoundary
    if (!boundary) {
      return {
        operatorId,
        title: 'Contains',
        summary: 'No boundary geometry available for this entity.',
        entityId: primary.id,
        entityName: primary.name,
        ranAt,
        rows: [{ label: 'Area', value: primary.name }],
        mapPoints: [],
        routeLine: null,
      }
    }

    const method = 'TIGERweb boundary'

    const areaJobs = jobs.filter((j) => pointInGeoJson({ lat: j.lat, lng: j.lng }, boundary))
    const areaAssets = assets.filter((a) => pointInGeoJson({ lat: a.lat, lng: a.lng }, boundary))
    const areaPeople = people.filter(
      (p) =>
        p.lat != null &&
        p.lng != null &&
        Number.isFinite(p.lat) &&
        Number.isFinite(p.lng) &&
        pointInGeoJson({ lat: p.lat, lng: p.lng }, boundary),
    )

    const plural = (n: number, one: string, many: string) =>
      `${n} ${n === 1 ? one : many}`

    return {
      operatorId,
      title: 'Contains',
      summary: `${plural(areaJobs.length, 'job', 'jobs')}, ${plural(areaAssets.length, 'asset', 'assets')}, ${plural(areaPeople.length, 'person', 'people')} inside ${primary.name}.`,
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [
        { label: 'Area', value: primary.name },
        { label: 'Method', value: method },
        { label: 'Jobs', value: String(areaJobs.length) },
        { label: 'Assets', value: String(areaAssets.length) },
        { label: 'People', value: String(areaPeople.length) },
      ],
      mapPoints: [
        ...areaJobs.map((j) =>
          mapPointForLocatedEntity({
            category: 'job',
            id: j.id,
            name: j.name,
            lat: j.lat,
            lng: j.lng,
            color: '#d97706',
            subtitle: j.number ? `Job · #${j.number}` : `Job · ${j.status}`,
          }),
        ),
        ...areaAssets.map((a) =>
          mapPointForLocatedEntity({
            category: 'asset',
            id: a.id,
            name: a.name,
            lat: a.lat,
            lng: a.lng,
            color: '#2563eb',
            subtitle: `Asset · ${a.assetKind}`,
          }),
        ),
        ...areaPeople.map((p) =>
          mapPointForLocatedEntity({
            category: 'person',
            id: p.id,
            name: p.name,
            lat: p.lat as number,
            lng: p.lng as number,
            color: p.personKind === 'employee' ? '#0d9488' : '#c026d3',
            subtitle:
              p.personKind === 'employee' ? 'Person · Employee' : 'Person · External',
          }),
        ),
      ],
      routeLine: null,
    }
  }

  if (operatorId === 'fuel-planning') {
    const rangeMiles = Math.max(10, Math.min(500, inputs.rangeMiles || 80))
    const dest = resolveDestinationPoint(inputs, catalogs, steps)
    const fuelLabel = primary.asset?.fuelLevel ? ` · fuel ${primary.asset.fuelLevel}` : ''

    if (dest) {
      const { stops, route } = demoFuelStopsAlongRoute(primary, dest, rangeMiles)
      const tripMiles = milesBetween(primary, dest)
      return {
        operatorId,
        title: 'Fuel planning',
        summary: `${stops.length} fuel stop${stops.length === 1 ? '' : 's'} along ~${Math.round(tripMiles)} mi route to ${dest.label}.`,
        entityId: primary.id,
        entityName: primary.name,
        ranAt,
        rows: [
          { label: 'Vehicle', value: `${primary.name}${fuelLabel}` },
          { label: 'Range', value: `${rangeMiles} mi` },
          { label: 'Destination', value: dest.label },
          { label: 'Trip distance', value: `~${Math.round(tripMiles)} mi` },
          { label: 'Stops', value: String(stops.length) },
        ],
        mapPoints: [
          ...stops,
          mapPointForDestination(dest, { subtitle: 'Destination' }),
        ],
        routeLine: route,
      }
    }

    const stops = demoFuelStopsAround(primary, rangeMiles)
    return {
      operatorId,
      title: 'Fuel planning',
      summary: `${stops.length} fuel stations within ~${rangeMiles} mi of ${primary.name}.`,
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [
        { label: 'Vehicle', value: `${primary.name}${fuelLabel}` },
        { label: 'Range', value: `${rangeMiles} mi` },
        { label: 'Destination', value: 'None (search area)' },
        { label: 'Stops', value: String(stops.length) },
      ],
      mapPoints: stops,
      routeLine: null,
    }
  }

  if (operatorId === 'forecast') {
    return runForecastOperation({
      primary,
      inputs,
      jobs,
      municipalityBoundary,
      ranAt,
    })
  }

  return null
}

const FORECAST_TYPE_LABEL: Record<ForecastType, string> = {
  active: 'Active',
  inactive: 'Inactive',
  starting: 'Starting',
  ending: 'Ending',
}

const FORECAST_TYPE_COLOR: Record<ForecastType, string> = {
  active: '#059669',
  inactive: '#64748b',
  starting: '#2563eb',
  ending: '#ea580c',
}

function isoDateInRange(day: string | null, rangeStart: string, rangeEnd: string): boolean {
  if (!day) return false
  return day >= rangeStart && day <= rangeEnd
}

function scheduleOverlapsRange(
  scheduleStart: string | null,
  scheduleEnd: string | null,
  rangeStart: string,
  rangeEnd: string,
): boolean {
  if (!scheduleStart && !scheduleEnd) return false
  const s = scheduleStart || scheduleEnd!
  const e = scheduleEnd || scheduleStart!
  return s <= rangeEnd && e >= rangeStart
}

function runForecastOperation(args: {
  primary: OperationEntity
  inputs: OperationInputs
  jobs: ControlCenterJob[]
  municipalityBoundary: GeoJSON.FeatureCollection | null
  ranAt: number
}): OperationResult {
  const { primary, inputs, jobs, municipalityBoundary, ranAt } = args
  const forecastType = inputs.forecastType || 'active'
  const rangeStart = String(inputs.forecastStartDate || '').trim().slice(0, 10)
  const rangeEnd = String(inputs.forecastEndDate || '').trim().slice(0, 10)

  if (!/^\d{4}-\d{2}-\d{2}$/.test(rangeStart) || !/^\d{4}-\d{2}-\d{2}$/.test(rangeEnd)) {
    return {
      operatorId: 'forecast',
      title: 'Jobs in area',
      summary: 'Choose a start and end date for the forecast range.',
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [{ label: 'Jurisdiction', value: primary.name }],
      mapPoints: [],
      routeLine: null,
    }
  }

  if (rangeStart > rangeEnd) {
    return {
      operatorId: 'forecast',
      title: 'Jobs in area',
      summary: 'Start date must be on or before end date.',
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [
        { label: 'Start', value: rangeStart },
        { label: 'End', value: rangeEnd },
      ],
      mapPoints: [],
      routeLine: null,
    }
  }

  if (!municipalityBoundary) {
    return {
      operatorId: 'forecast',
      title: 'Jobs in area',
      summary: 'Municipality boundary is not loaded yet — wait for the outline, then run again.',
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [{ label: 'Jurisdiction', value: primary.name }],
      mapPoints: [],
      routeLine: null,
    }
  }

  const inside = jobs.filter((job) => pointInGeoJson({ lat: job.lat, lng: job.lng }, municipalityBoundary))

  const matches = inside.filter((job) => {
    const start = job.scheduleStart
    const end = job.scheduleEnd
    switch (forecastType) {
      case 'active':
        return scheduleOverlapsRange(start, end, rangeStart, rangeEnd)
      case 'inactive':
        return !scheduleOverlapsRange(start, end, rangeStart, rangeEnd)
      case 'starting':
        return isoDateInRange(start, rangeStart, rangeEnd)
      case 'ending':
        return isoDateInRange(end, rangeStart, rangeEnd)
      default:
        return false
    }
  })

  const color = FORECAST_TYPE_COLOR[forecastType]
  const typeLabel = FORECAST_TYPE_LABEL[forecastType]
  const limited = matches.slice(0, 80)

  return {
    operatorId: 'forecast',
    title: 'Jobs in area',
    summary: `${matches.length} job${matches.length === 1 ? '' : 's'} ${typeLabel.toLowerCase()} in ${primary.name} (${rangeStart} → ${rangeEnd}).${
      matches.length > limited.length ? ` Showing first ${limited.length}.` : ''
    }`,
    entityId: primary.id,
    entityName: primary.name,
    ranAt,
    rows: [
      { label: 'Jurisdiction', value: primary.name },
      { label: 'Type', value: typeLabel },
      { label: 'Range', value: `${rangeStart} → ${rangeEnd}` },
      { label: 'Jobs in boundary', value: String(inside.length) },
      { label: 'Matches', value: String(matches.length) },
    ],
    mapPoints: limited.map((job) =>
      mapPointForLocatedEntity({
        category: 'job',
        id: job.id,
        name: job.name,
        lat: job.lat,
        lng: job.lng,
        color,
        subtitle: [
          job.number ? `#${job.number}` : null,
          job.scheduleStart || job.scheduleEnd
            ? `${job.scheduleStart || '—'} → ${job.scheduleEnd || '—'}`
            : 'No schedule',
        ]
          .filter(Boolean)
          .join(' · '),
      }),
    ),
    routeLine: null,
  }
}

const STATE_FIPS_TO_ABBR: Record<string, string> = {
  '04': 'AZ',
  '08': 'CO',
  '16': 'ID',
  '30': 'MT',
  '31': 'NE',
  '32': 'NV',
  '35': 'NM',
  '38': 'ND',
  '41': 'OR',
  '49': 'UT',
  '53': 'WA',
  '56': 'WY',
}

function normalizeJurisdictionName(value: string): string {
  return String(value || '')
    .toLowerCase()
    .replace(/\b(city|town|village|borough|county|municipality|cdp)\b/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

function namesLooselyMatch(a: string, b: string): boolean {
  const na = normalizeJurisdictionName(a)
  const nb = normalizeJurisdictionName(b)
  if (!na || !nb) return false
  return na === nb || na.includes(nb) || nb.includes(na)
}

function isLicenseExpired(isoDate: string): boolean {
  if (!isoDate) return false
  const d = new Date(`${isoDate}T12:00:00`)
  if (Number.isNaN(d.getTime())) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return d.getTime() < today.getTime()
}

function isLicenseExpiringSoon(isoDate: string, withinDays = 90): boolean {
  if (!isoDate) return false
  const d = new Date(`${isoDate}T12:00:00`)
  if (Number.isNaN(d.getTime())) return false
  const now = new Date()
  const diff = d.getTime() - now.getTime()
  return diff >= 0 && diff <= withinDays * 24 * 60 * 60 * 1000
}

function licenseFreshness(m: ControlCenterMunicipality): {
  status: 'reciprocity' | 'current' | 'expiring' | 'expired' | 'unknown'
  detail: string
} {
  const number = String(m.licenseNumber || '').trim().toLowerCase()
  if (number === 'reciprocity' || (m.qualifier || '').toLowerCase().includes('reciprocity')) {
    return { status: 'reciprocity', detail: 'Reciprocity with state-level' }
  }
  if (!m.expirationDate) {
    return { status: 'unknown', detail: `${licenseTypeLabel(m.licenseType)} · #${m.licenseNumber || '—'} · no expiration` }
  }
  if (isLicenseExpired(m.expirationDate)) {
    return {
      status: 'expired',
      detail: `${licenseTypeLabel(m.licenseType)} · #${m.licenseNumber} · expired ${m.expirationDate}`,
    }
  }
  if (isLicenseExpiringSoon(m.expirationDate)) {
    return {
      status: 'expiring',
      detail: `${licenseTypeLabel(m.licenseType)} · #${m.licenseNumber} · expires ${m.expirationDate}`,
    }
  }
  return {
    status: 'current',
    detail: `${licenseTypeLabel(m.licenseType)} · #${m.licenseNumber} · expires ${m.expirationDate}`,
  }
}

function matchLicensesForJurisdiction(
  municipalities: ControlCenterMunicipality[],
  level: ControlCenterMunicipalityLevel,
  jurisdiction: ResolvedJurisdiction,
  stateAbbr: string | null,
): ControlCenterMunicipality[] {
  return municipalities.filter((m) => {
    if (m.level !== level) return false
    if (jurisdiction.geoid && m.censusGeoid && m.censusGeoid === jurisdiction.geoid) return true
    if (stateAbbr && m.state !== stateAbbr) return false
    return (
      namesLooselyMatch(m.name, jurisdiction.name) ||
      namesLooselyMatch(m.name, jurisdiction.basename)
    )
  })
}

function levelRollupStatus(
  licenses: ControlCenterMunicipality[],
): 'reciprocity' | 'current' | 'expiring' | 'expired' | 'unknown' {
  const statuses = licenses.map((m) => licenseFreshness(m).status)
  if (statuses.includes('expired')) return 'expired'
  if (statuses.includes('expiring')) return 'expiring'
  if (statuses.includes('unknown')) return 'unknown'
  if (statuses.every((s) => s === 'reciprocity')) return 'reciprocity'
  if (statuses.includes('reciprocity') || statuses.includes('current')) return 'current'
  return 'unknown'
}

const LEVEL_STATUS_COLOR: Record<string, string> = {
  reciprocity: '#2563eb',
  current: '#059669',
  expiring: '#d97706',
  expired: '#dc2626',
  unknown: '#64748b',
  missing: '#94a3b8',
  none: '#cbd5e1',
}

/**
 * Resolve Census jurisdictions at the entity point and report license coverage
 * for state / county / city (missing data when we lack a matching license row).
 */
export async function runLocalLicenseCheckOperation(args: {
  primary: OperationEntity
  municipalities: ControlCenterMunicipality[]
}): Promise<OperationResult> {
  const { primary, municipalities } = args
  const ranAt = Date.now()

  if (!Number.isFinite(primary.lat) || !Number.isFinite(primary.lng)) {
    return {
      operatorId: 'local-license-check',
      title: 'Local license check',
      summary: 'Selected entity has no usable coordinates.',
      entityId: primary.id,
      entityName: primary.name,
      ranAt,
      rows: [],
      mapPoints: [],
      routeLine: null,
    }
  }

  const resolved = await resolveJurisdictionsAtPoint(primary.lat, primary.lng)
  const stateAbbr =
    resolved.state?.stateAbbr ||
    (resolved.state?.stateFips ? STATE_FIPS_TO_ABBR[resolved.state.stateFips] : null) ||
    (resolved.county?.stateFips ? STATE_FIPS_TO_ABBR[resolved.county.stateFips] : null) ||
    (resolved.city?.stateFips ? STATE_FIPS_TO_ABBR[resolved.city.stateFips] : null) ||
    primary.job?.state ||
    primary.municipality?.state ||
    null

  type LevelOutcome = {
    level: TigerwebBoundaryLevel
    label: string
    jurisdictionLabel: string
    statusLabel: string
    statusKey: string
    licenses: ControlCenterMunicipality[]
  }

  const outcomes: LevelOutcome[] = []

  const evaluateLevel = (
    level: TigerwebBoundaryLevel,
    jurisdiction: ResolvedJurisdiction | null,
  ) => {
    const title = level.charAt(0).toUpperCase() + level.slice(1)
    if (!jurisdiction) {
      outcomes.push({
        level,
        label: title,
        jurisdictionLabel:
          level === 'city' ? 'No incorporated city at this point' : 'Could not resolve',
        statusLabel: level === 'city' ? 'No city jurisdiction' : 'Unresolved',
        statusKey: 'none',
        licenses: [],
      })
      return
    }

    const matches = matchLicensesForJurisdiction(
      municipalities,
      level,
      jurisdiction,
      stateAbbr,
    )
    if (matches.length === 0) {
      outcomes.push({
        level,
        label: title,
        jurisdictionLabel: `${jurisdiction.name} (${jurisdiction.geoid})`,
        statusLabel: 'Missing data',
        statusKey: 'missing',
        licenses: [],
      })
      return
    }

    const rollup = levelRollupStatus(matches)
    const statusLabel =
      rollup === 'reciprocity'
        ? 'Reciprocity'
        : rollup === 'current'
          ? 'Current'
          : rollup === 'expiring'
            ? 'Expiring soon'
            : rollup === 'expired'
              ? 'Expired'
              : 'Unknown'

    outcomes.push({
      level,
      label: title,
      jurisdictionLabel: `${jurisdiction.name} (${jurisdiction.geoid})`,
      statusLabel,
      statusKey: rollup,
      licenses: matches,
    })
  }

  evaluateLevel('state', resolved.state)
  evaluateLevel('county', resolved.county)
  evaluateLevel('city', resolved.city)

  const missing = outcomes.filter((o) => o.statusKey === 'missing').length
  const expired = outcomes.filter((o) => o.statusKey === 'expired').length
  const expiring = outcomes.filter((o) => o.statusKey === 'expiring').length
  const covered = outcomes.filter((o) =>
    ['current', 'reciprocity', 'expiring', 'expired', 'unknown'].includes(o.statusKey),
  ).length

  let summary = `${covered} level${covered === 1 ? '' : 's'} with licenses`
  if (missing) summary += ` · ${missing} missing data`
  if (expired) summary += ` · ${expired} expired`
  else if (expiring) summary += ` · ${expiring} expiring soon`

  const rows: Array<{ label: string; value: string }> = [
    { label: 'Location', value: `${primary.lat.toFixed(5)}, ${primary.lng.toFixed(5)}` },
    { label: 'State abbr', value: stateAbbr || '—' },
  ]

  for (const outcome of outcomes) {
    rows.push({
      label: outcome.label,
      value: `${outcome.jurisdictionLabel} — ${outcome.statusLabel}`,
    })
    for (const lic of outcome.licenses) {
      const fresh = licenseFreshness(lic)
      rows.push({
        label: `  ${outcome.label} · ${licenseTypeLabel(lic.licenseType as ControlCenterLicenseType)}`,
        value: fresh.detail,
      })
    }
  }

  const mapPoints: OperationMapPoint[] = []
  const seenMuniIds = new Set<string>()

  if (
    primary.kind === 'job' ||
    primary.kind === 'person' ||
    primary.kind === 'asset' ||
    primary.kind === 'municipality'
  ) {
    mapPoints.push(
      mapPointForLocatedEntity({
        category: primary.kind,
        id: primary.id,
        name: primary.name,
        lat: primary.lat,
        lng: primary.lng,
        color: '#0f172a',
        subtitle: 'Check origin',
      }),
    )
    if (primary.kind === 'municipality') seenMuniIds.add(primary.id)
  } else {
    mapPoints.push({
      id: `llc-origin-${primary.id}`,
      name: primary.name,
      subtitle: 'Check origin',
      lat: primary.lat,
      lng: primary.lng,
      color: '#0f172a',
    })
  }

  for (const outcome of outcomes) {
    for (const m of outcome.licenses) {
      if (seenMuniIds.has(m.id)) continue
      seenMuniIds.add(m.id)
      mapPoints.push(
        mapPointForLocatedEntity({
          category: 'municipality',
          id: m.id,
          name: m.name,
          lat: m.lat,
          lng: m.lng,
          color: LEVEL_STATUS_COLOR[outcome.statusKey] || '#64748b',
          subtitle: `${outcome.label} · ${outcome.statusLabel}`,
        }),
      )
    }
  }

  return {
    operatorId: 'local-license-check',
    title: 'Local license check',
    summary,
    entityId: primary.id,
    entityName: primary.name,
    ranAt,
    rows,
    mapPoints,
    routeLine: null,
  }
}
